import React from "react";
import Call from "@/model/interface/Call";
import { Badge } from "@/components/ui/badge";
import { StickyNote, Voicemail } from "lucide-react";
import { RecordingSource } from "@/enum/RecordingSource";
import VoicemailPlayer from "@/components/messages/VoicemailPlayer";
import CallSummaryBlock from "@/components/calls/CallSummaryBlock";
import {
  formatDuration,
  formatTime,
  getCallIcon,
  getUnansweredBadge,
} from "@/util/call";

interface CallActivityItemProps {
  call: Call;
  onOpen?: () => void;
  hideVoicemailPlayer?: boolean;
}

const CallActivityItem: React.FC<CallActivityItemProps> = ({ call, onOpen, hideVoicemailPlayer = false }) => {
  const unansweredBadge = getUnansweredBadge(call.direction, call.status);
  const isVoicemail = call.recording?.source === RecordingSource.VOICEMAIL;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!onOpen) {
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onOpen();
    }
  };

  const stopPropagation = (event: React.SyntheticEvent) => {
    event.stopPropagation();
  };

  return (
    <div
      className={`px-4 py-3 hover:bg-accent/50 transition-colors ${onOpen ? "cursor-pointer" : ""}`}
      onClick={onOpen}
      onKeyDown={handleKeyDown}
      role={onOpen ? "button" : undefined}
      tabIndex={onOpen ? 0 : undefined}
      title={onOpen ? "Open call" : undefined}
    >
      <div className="flex items-center gap-3">
        <div className="w-8 flex justify-center shrink-0">
          {isVoicemail ? <Voicemail className="h-4 w-4 text-info" /> : getCallIcon(call.direction, call.status)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium">
              {isVoicemail ? "Voicemail" : call.direction === "INBOUND" ? "Incoming call" : "Outgoing call"}
            </span>
            {!isVoicemail && unansweredBadge}
            {call.disposition && (
              <Badge
                variant="outline"
                className="text-xs"
                style={{ borderColor: call.disposition.color, color: call.disposition.color }}
              >
                {call.disposition.name}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>{call.phoneNumber}</span>
            {call.duration != null && call.duration > 0 && (
              <>
                <span>·</span>
                <span className="tabular-nums">{formatDuration(call.duration)}</span>
              </>
            )}
          </div>
        </div>
        <span className="text-xs text-muted-foreground shrink-0 tabular-nums">
          {formatTime(new Date(call.createdAt!))}
        </span>
      </div>
      {call.notes && (
        <div className="flex items-start gap-2 mt-2 ml-11">
          <StickyNote className="h-3 w-3 text-muted-foreground mt-0.5 shrink-0" />
          <p className="text-xs text-muted-foreground line-clamp-2">{call.notes}</p>
        </div>
      )}
      {call.recording?.summary && call.id && (
        <div className="mt-2 ml-11" onClick={stopPropagation} onKeyDown={stopPropagation}>
          <CallSummaryBlock callId={call.id} summary={call.recording.summary} />
        </div>
      )}
      {isVoicemail && !hideVoicemailPlayer && (
        <div className="mt-2 ml-11" onClick={stopPropagation} onKeyDown={stopPropagation}>
          <VoicemailPlayer call={call} />
        </div>
      )}
    </div>
  );
};

export default CallActivityItem;
